import { ColumnDef } from "@tanstack/react-table";
import { SortableColumn } from "@/components/shared/sortable-column";
import { CommandAgent } from "@/types/command-agent";
import Link from "next/link";
import { CommandAgentActionCell } from "@/components/command-team/command-agent/tables/command-agent-actions";
import { formatDateTime } from "@/utils/global";
import { cn } from "@/lib/utils";
import { isTeamVisibility } from "@/types/visibility";

interface AgentNameCellProps {
    row: { original: CommandAgent; getValue: (key: string) => any };
}

const AgentNameCell: React.FC<AgentNameCellProps> = ({ row }) => {
    const name = row.getValue("name") as string;
    const description = row.original.description;

    return (
        <div className="flex min-w-0 flex-col pl-4">
            <Link
                href={`/workforce/command-team-old/agents/${row.original.id}?name=${encodeURIComponent(name)}`}
                className="truncate text-sm font-semibold transition-colors hover:text-primary"
            >
                {name}
            </Link>
            {description && (
                <span className="mt-0.5 truncate text-[13px] text-muted-foreground">
                    {description.length > 70 ? description.slice(0, 70) + '…' : description}
                </span>
            )}
        </div>
    );
};

export function StatusBadge({ isActive }: { isActive?: boolean }) {
    return (
        <span className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            isActive
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
        )}>
            <span className={cn(
                "h-1.5 w-1.5 rounded-full",
                isActive ? "bg-emerald-500" : "bg-zinc-400"
            )} />
            {isActive ? "Active" : "Inactive"}
        </span>
    );
}

export function VisibilityBadge({
    visibility,
}: {
    visibility?: AppIntegrationVisibility;
}) {
    const isTeam = isTeamVisibility(visibility ?? "PERSONAL");
    return (
        <span className={cn(
            "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold",
            isTeam
                ? "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400"
                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
        )}>
            {isTeam ? "Team" : "Personal"}
        </span>
    );
}

export function getCommandAgentColumns({
    handleSort,
    sortBy,
    sortOrder,
}: {
    handleSort: (column: string) => void;
    sortBy: string;
    sortOrder: string;
}): ColumnDef<CommandAgent>[] {
    return [
        {
            accessorKey: "name",
            header: () => (
                <SortableColumn
                    label="Agent Name"
                    columnKey="name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => <AgentNameCell row={row} />,
        },
        {
            accessorKey: "is_active",
            header: () => (
                <span className="inline-flex items-center pl-4 text-sm font-medium">Status</span>
            ),
            cell: ({ row }) => (
                <div className="pl-4">
                    <StatusBadge isActive={row.original.is_active} />
                </div>
            ),
        },
        {
            accessorKey: "visibility",
            header: () => (
                <span className="inline-flex items-center pl-4 text-sm font-medium">Visibility</span>
            ),
            cell: ({ row }) => (
                <div className="pl-4">
                    <VisibilityBadge visibility={row.original.visibility} />
                </div>
            ),
        },
        {
            accessorKey: "created_at",
            header: () => (
                <SortableColumn
                    label="Created At"
                    columnKey="created_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const date = row.getValue("created_at") as string;
                const { date: dateStr, time } = formatDateTime(date);
                return (
                    <div className="flex flex-col pl-4">
                        <span className="text-sm">{dateStr}</span>
                        <span className="text-xs text-muted-foreground">{time}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "updated_at",
            header: () => (
                <SortableColumn
                    label="Updated At"
                    columnKey="updated_at"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />
            ),
            cell: ({ row }) => {
                const date = row.getValue("updated_at") as string;
                const { date: dateStr, time } = formatDateTime(date);
                return (
                    <div className="flex flex-col pl-4">
                        <span className="text-sm">{dateStr}</span>
                        <span className="text-xs text-muted-foreground">{time}</span>
                    </div>
                );
            },
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => <CommandAgentActionCell row={row} />,
        },
    ];
}
